/* eslint-disable react-hooks/exhaustive-deps */
import { Form, Modal, Tabs } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { HooRequestStatusEnum } from 'apps/mod/src/models/hoo-request-status-enum';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkIsDefaultScheduleChangeRequestor,
  getReadTabsPermissions,
  getUserEmailAddress,
  getUserIsMODAdmin,
  getUserPositions,
  getWriteTabsPermissions,
} from '../../store/user/user-reducer';
import GenericGrid from '../grid/GenericGrid/GenericGrid';
import hooAccommodationsColumns, { hooColumnOrder } from '../grid/grid-configs/hoo-accommodations-columns';
import SeachHooRequests from '../header/search/SeachHooRequests';
import CallForwardingSnapshot from '../CallForwardingSnapshot/CallForwardingSnapshot';
import { CALL_FORWARDING_AUTHORIZED_POSITIONS } from '../../constants/permissions.constants';
import {
  acceptHooAccommodation,
  deleteHooAccommodation,
  rejectHooAccommodation,
  searchHoo,
  setSearchHoo,
  exportHooRequests,
} from './actions';
import { DEFAULT_GRID_SIZE } from 'libs/util/src/lib/constants/common.constants';
import AddHooRequestDrawer from './AddHooRequestDrawer';
import { getHooAccomodations, getLoading, getSearchHooRequests, getExportLoading } from './selectors';
import { ExportImportButton, ExportImportButtonText } from '@med-fe/ui';

const TEMPORARY_REQUEST_DEFAULT_PAGE_SIZE = 100;
const TEMPORARY_REQUEST_DEFAULT_SORTING = [{ id: 'EffectiveDate', desc: true }];

function HooAccommodations() {
  const dispatch = useDispatch();
  const loading = useSelector(getLoading);
  const exportLoading = useSelector(getExportLoading);
  const gridColumns = hooAccommodationsColumns ?? [];
  const data = useSelector(getHooAccomodations);
  const search = useSelector(getSearchHooRequests);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAccept, setOpenAccept] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [openRejectReason, setOpenRejectReason] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const userEmailAddress = useSelector(getUserEmailAddress);
  const isDefaultScheduleChangeRequestor = useSelector(checkIsDefaultScheduleChangeRequestor);
  const [rejectReasonForm] = Form.useForm();
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeHooType, setActiveHooType] = useState<'StandardHoo' | 'UnPlannedHoo'>('StandardHoo');
  const temporaryData =
    activeHooType === 'UnPlannedHoo'
      ? [...data].sort(
          (first, second) => moment(second.EffectiveDate).valueOf() - moment(first.EffectiveDate).valueOf()
        )
      : data;
  const [activeTab, setActiveTab] = useState<'Standard' | 'UnPlanned' | 'CallForwarding'>('Standard');
  // Grid filter and sorting state for export
  const [gridColumnFilters, setGridColumnFilters] = useState<any[]>([]);
  const [gridSorting, setGridSorting] = useState<any[]>(TEMPORARY_REQUEST_DEFAULT_SORTING);
  const userPositions = useSelector(getUserPositions);
  const userIsMODAdmin = useSelector(getUserIsMODAdmin);
  const readTabsPermissions = useSelector(getReadTabsPermissions);
  const writeTabsPermissions = useSelector(getWriteTabsPermissions);
  const isStandardHooReadAllowed = readTabsPermissions?.standard == true;
  const isStandardHooWriteAllowed = writeTabsPermissions?.standard == true;
  const isUnplannedHooReadAllowed = readTabsPermissions?.unplanned == true;
  const isUnplannedHooWriteAllowed = writeTabsPermissions?.unplanned == true;
  const isStandardHooRequestAllowed =
    userPositions?.includes('RCFD') ||
    userPositions?.includes('AV') ||
    userPositions?.includes('RM') ||
    isDefaultScheduleChangeRequestor ||
    userIsMODAdmin;

  const isUnPlannedHooRequestAllowed =
    userPositions?.includes('DM') ||
    userPositions?.includes('AV') ||
    userPositions?.includes('RM') ||
    userPositions?.includes('AM') ||
    userIsMODAdmin;

  // Tab visibility based on permissions
  const showStandardTab = isStandardHooRequestAllowed || isStandardHooReadAllowed || isStandardHooWriteAllowed;
  const showUnplannedTab = isUnPlannedHooRequestAllowed || isUnplannedHooReadAllowed || isUnplannedHooWriteAllowed;

  // Call Forwarding tab visibility
  const isCallForwardingReadAllowed = readTabsPermissions?.callForwarding == true;
  const isCallForwardingWriteAllowed = writeTabsPermissions?.callForwarding == true;
  const showCallForwardingTab =
    userIsMODAdmin ||
    CALL_FORWARDING_AUTHORIZED_POSITIONS.some((pos) => userPositions?.includes(pos)) ||
    isCallForwardingReadAllowed ||
    isCallForwardingWriteAllowed;

  // Keep a ref to the active hoo type so search effect can read the latest
  // value without needing `activeHooType` in its dependency array
  const activeHooTypeRef = useRef(activeHooType);
  useEffect(() => {
    activeHooTypeRef.current = activeHooType;
  }, [activeHooType]);

  // Ensure the active tab matches available permissions. If the user only has
  // access to UnPlanned, switch to that tab automatically.
  useEffect(() => {
    if (showUnplannedTab && !showStandardTab) {
      setActiveHooType('UnPlannedHoo');
      setActiveTab('UnPlanned');
    } else if (showStandardTab && !showUnplannedTab) {
      setActiveHooType('StandardHoo');
      setActiveTab('Standard');
    }
    // If both are available, keep current active tab.
  }, [showStandardTab, showUnplannedTab]);

  useEffect(() => {
    // include HooType when searching. Depend only on `search` to avoid
    // triggering a search twice when both `search` and `activeHooType` update
    const payload = { ...(search || {}), ScheduleType: activeHooTypeRef.current };
    dispatch(searchHoo(payload));
  }, [search]);

  useEffect(() => {
    // when hoo type tab changes, update stored search statuses only if they differ
    const statusesToSet =
      activeHooType === 'UnPlannedHoo'
        ? [HooRequestStatusEnum.PENDING, HooRequestStatusEnum.COMPLETED]
        : [HooRequestStatusEnum.PENDING];
    const pageSizeToSet = activeHooType === 'UnPlannedHoo' ? TEMPORARY_REQUEST_DEFAULT_PAGE_SIZE : DEFAULT_GRID_SIZE;
    const currentStatuses = (search && search.statuses) || [];
    const normalize = (arr: any[]) => (arr || []).slice().sort().join(',');
    if (normalize(currentStatuses) !== normalize(statusesToSet) || search.size !== pageSizeToSet) {
      // update stored search filters so the UI and subsequent actions reflect the change
      dispatch(setSearchHoo({ statuses: statusesToSet, size: pageSizeToSet, page: 0 }));
    }
    // Do NOT dispatch `searchHoo` from here - searching is centralized in the effect
    // that listens for `search` updates. This effect intentionally depends only on
    // `activeHooType` (and reads latest `search`) to avoid racing and duplicate calls.
  }, [activeHooType]);

  useEffect(() => {
    // Suppress benign ResizeObserver loop errors which can bubble as uncaught errors
    const handler = (event: ErrorEvent) => {
      if (event && event.message && event.message.includes('ResizeObserver loop')) {
        try {
          event.stopImmediatePropagation();
        } catch (e) {
          /* ignore */
        }
      }
    };
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const reason: any = (event && ((event as any).reason || (event as any).detail)) || '';
      const msg = typeof reason === 'string' ? reason : reason?.message;
      if (msg && msg.includes && msg.includes('ResizeObserver loop')) {
        try {
          event.preventDefault();
        } catch (e) {
          /* ignore */
        }
      }
    };
    window.addEventListener('error', handler, true);
    window.addEventListener('unhandledrejection', rejectionHandler);

    // Also override window.onerror to return true for ResizeObserver loop errors
    const originalOnError = (window as any).onerror;
    (window as any).onerror = function (message: any, source?: any, lineno?: any, colno?: any, error?: any) {
      try {
        const msg = typeof message === 'string' ? message : error?.message || '';
        if (msg && msg.toString().includes && msg.toString().includes('ResizeObserver loop')) {
          return true; // prevent default handlers (suppress overlay)
        }
      } catch (e) {
        // ignore
      }
      if (typeof originalOnError === 'function') {
        try {
          return originalOnError.apply(window, [message, source, lineno, colno, error]);
        } catch (e) {
          return false;
        }
      }
      return false;
    };

    // Additionally, wrap ResizeObserver prototype methods to guard against the "ResizeObserver loop" exception
    const winAny: any = window as any;
    const roProto = winAny?.ResizeObserver?.prototype;
    const originalObserve = roProto?.observe;
    const originalUnobserve = roProto?.unobserve;
    const originalDisconnect = roProto?.disconnect;

    if (roProto && originalObserve) {
      try {
        roProto.observe = function (...args: any[]) {
          try {
            return originalObserve.apply(this, args);
          } catch (e) {
            // swallow benign ResizeObserver errors
            return undefined;
          }
        };
        if (originalUnobserve) {
          roProto.unobserve = function (...args: any[]) {
            try {
              return originalUnobserve.apply(this, args);
            } catch (e) {
              return undefined;
            }
          };
        }
        if (originalDisconnect) {
          roProto.disconnect = function (...args: any[]) {
            try {
              return originalDisconnect.apply(this, args);
            } catch (e) {
              return undefined;
            }
          };
        }
      } catch (e) {
        // ignore patching errors
      }
    }

    return () => {
      window.removeEventListener('error', handler, true);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      // restore original onerror
      try {
        (window as any).onerror = originalOnError;
      } catch (e) {
        // ignore
      }
      // restore originals
      if (roProto && originalObserve) {
        try {
          roProto.observe = originalObserve;
          if (originalUnobserve) roProto.unobserve = originalUnobserve;
          if (originalDisconnect) roProto.disconnect = originalDisconnect;
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  const editDrawer = (record: any) => {
    setModalData(record);
    setIsDrawerOpen(true);
  };

  const deleteTemplate = (record: any) => {
    setDeleteId(record);
    setOpenDelete(true);
  };

  const onRequestAccept = (record: any) => {
    setDeleteId(record);
    setOpenAccept(true);
  };

  const onRequestDecline = (record: any) => {
    // Implement decline logic here
    setDeleteId(record);
    setOpenReject(true);
  };

  const handleAccept = (e: any) => {
    dispatch(
      acceptHooAccommodation({ requestId: deleteId, search: { ...(search || {}), ScheduleType: activeHooType } })
    );
    setOpenAccept(false);
    setDeleteId(null);
  };

  const handleAcceptCancel = (e: any) => {
    setOpenAccept(false);
    setDeleteId(null);
  };

  const handleReject = (e: any) => {
    setOpenReject(false);
    setRejectReason('');
    rejectReasonForm.resetFields();
    setOpenRejectReason(true);
  };

  const handleRejectReasonOk = (e: any) => {
    dispatch(
      rejectHooAccommodation({
        requestId: deleteId,
        search: { ...(search || {}), ScheduleType: activeHooType },
        rejectReason,
      })
    );
    setOpenRejectReason(false);
    setRejectReason('');
    setDeleteId(null);
  };

  const handleRejectCancel = (e: any) => {
    setOpenReject(false);
    setOpenRejectReason(false);
    setRejectReason('');
    setDeleteId(null);
  };

  let filterValues = hooAccommodationsColumns(
    activeHooType,
    activeHooType === 'UnPlannedHoo' && isUnPlannedHooRequestAllowed,
    userEmailAddress,
    isDefaultScheduleChangeRequestor,
    activeHooType === 'UnPlannedHoo'
      ? isUnPlannedHooRequestAllowed || isUnplannedHooWriteAllowed
      : isStandardHooRequestAllowed || isStandardHooWriteAllowed,
    search.statuses || [],
    editDrawer,
    deleteTemplate,
    onRequestAccept,
    onRequestDecline
  );

  // Precompute Unplanned columns to avoid using an IIFE inside JSX
  const unplannedFinalCols = (() => {
    try {
      let unplannedColsBase = [...(filterValues || [])];

      const hasEnd = unplannedColsBase.find((c) => (c.id || c.accessorKey) === 'EndDate');
      if (!hasEnd) {
        unplannedColsBase.push({
          id: 'EndDate',
          header: 'END DATE',
          accessorKey: 'EndDate',
          enableColumnFilter: true,
          filterFn: 'contains',
          size: 140,
          Cell: ({ row }: any) => (row?.original?.EndDate ? row.original.EndDate.split('T')[0] : ''),
        });
      }

      const hasReq = unplannedColsBase.find((c) => (c.id || c.accessorKey) === 'RequestType');
      if (!hasReq) {
        unplannedColsBase.push({
          id: 'RequestType',
          header: 'REQUEST TYPE',
          accessorKey: 'RequestType',
          enableColumnFilter: true,
          filterFn: 'contains',
          size: 140,
        });
      }

      const desiredOrder = [
        'ScheduleChangeRequestId',
        'RequestedBy',
        'Requestor',
        'CreatedDate',
        'EffectiveDate',
        'EndDate',
        'RequestType',
        'Location',
        'Status',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'Reason',
        'OfficeNumber',
        'Actions',
      ];

      const orderedCols = desiredOrder
        .map((key) => {
          const col = unplannedColsBase.find((c) => (c.id || c.accessorKey) === key);
          if (!col) return null;
          if ((col.id || col.accessorKey) === 'EffectiveDate') {
            return {
              ...col,
              header: 'START DATE',
              Cell: ({ row }: any) => (row?.original?.EffectiveDate ? row.original.EffectiveDate.split('T')[0] : ''),
            };
          }
          if ((col.id || col.accessorKey) === 'EndDate') {
            return {
              ...col,
              header: 'END DATE',
              Cell: ({ row }: any) => (row?.original?.EndDate ? row.original.EndDate.split('T')[0] : ''),
            };
          }
          return col;
        })
        .filter(Boolean) as any[];

      const remainingCols = unplannedColsBase.filter(
        (c) => !orderedCols.find((oc) => (oc?.id || oc?.accessorKey) === (c.id || c.accessorKey))
      );
      return [...orderedCols, ...remainingCols];
    } catch (e) {
      return filterValues || [];
    }
  })();

  const handleDelete = async (e: any) => {
    dispatch(
      await deleteHooAccommodation({ requestId: deleteId, search: { ...(search || {}), ScheduleType: activeHooType } })
    );
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleDeleteCancel = (e: any) => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  // Drawer handlers
  const handleAddClick = () => {
    setModalData(null);
    setIsDrawerOpen(true);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setModalData(null);
  };
  const handleDrawerSubmit = (values: any) => {
    // Dispatch your create request action here
    dispatch(searchHoo({ ...(search || {}), ScheduleType: activeHooType }));
    setIsDrawerOpen(false);
    setModalData(null);
  };

  const handleExport = () => {
    if (!exportLoading) {
      // Convert column filters to a format suitable for the API
      const columnFiltersObj = gridColumnFilters.reduce((acc, filter) => {
        acc[filter.id] = filter.value;
        return acc;
      }, {});

      // Convert sorting to a format suitable for the API
      const sortingObj =
        gridSorting.length > 0
          ? activeHooType === 'UnPlannedHoo'
            ? {
                Sorting: {
                  Field: gridSorting[0].id,
                  Order: gridSorting[0].desc ? 'desc' : 'asc',
                },
              }
            : {
                sortBy: gridSorting[0].id,
                sortOrder: gridSorting[0].desc ? 'desc' : 'asc',
              }
          : activeHooType === 'UnPlannedHoo'
          ? {
              Sorting: {
                Field: 'EffectiveDate',
                Order: 'desc',
              },
            }
          : {};

      const exportPayload = {
        ...(search || {}),
        ScheduleType: activeHooType,
        ...columnFiltersObj,
        ...sortingObj,
      };
      dispatch(exportHooRequests(exportPayload));
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as 'Standard' | 'UnPlanned' | 'CallForwarding');
    if (key === 'Standard') setActiveHooType('StandardHoo');
    else if (key === 'UnPlanned') setActiveHooType('UnPlannedHoo');
  };

  return (
    <React.Fragment>
      {activeTab !== 'CallForwarding' && (
        <SeachHooRequests
          key={activeHooType}
          initialStatuses={activeHooType === 'UnPlannedHoo' ? ['PENDING', 'COMPLETED'] : ['PENDING']}
        />
      )}
      <Tabs activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: 12 }}>
        {showStandardTab && (
          <Tabs.TabPane tab='Permanent Hours Request' key='Standard'>
            {gridColumns.length > 0 && (
              <GenericGrid
                columns={filterValues}
                columnOrder={hooColumnOrder}
                data={data}
                enableRowSelection={false}
                rowSelection={[]}
                columnPinning={{}}
                filterDisplayMode='popover'
                highlightFilterMatch={true}
                paginationDisplayMode='pages'
                paginationProps={{
                  color: 'primary',
                  shape: 'rounded',
                  showRowsPerPage: true,
                  variant: 'outlined',
                  rowsPerPageOptions: [10, 50, 100, 500, 1000],
                }}
                enableStickyHeader={true}
                enableStickyFooter={true}
                enableColumnPinning={true}
                isLoading={loading}
                enableAdd={isStandardHooRequestAllowed || isStandardHooWriteAllowed}
                onAddClick={handleAddClick}
                muiTableBodyCellProps={{
                  sx: {
                    fontSize: '11px',
                    textAlign: 'left',
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                    padding: '6px 10px',
                    height: '40px',
                    lineHeight: '1.2',
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    fontSize: '11px',
                    fontWeight: 700,
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                    textAlign: 'center',
                    padding: '6px 10px',
                    height: '40px',
                    lineHeight: '1.2',
                  },
                }}
                muiTableBodyRowProps={{
                  sx: {
                    height: '40px',
                    '& td': {
                      padding: '6px 10px',
                      height: '40px',
                    },
                    textAlign: 'center',
                  },
                }}
                totalRecordsLabel='TOTAL REQUESTS'
              />
            )}
          </Tabs.TabPane>
        )}
        {showUnplannedTab && (
          <Tabs.TabPane tab='Temporary Hours Request' key='UnPlanned'>
            {/* For UnPlanned Hoo we filter columns to show Start (EffectiveDate), EndDate and RequestType */}
            {(() => {
              // start from the full set of columns so we can reorder but preserve weekdays and actions
              let unplannedColsBase = [...(filterValues || [])];

              // Ensure EndDate exists with formatted cell
              const hasEnd = unplannedColsBase.find((c) => (c.id || c.accessorKey) === 'EndDate');
              if (!hasEnd) {
                unplannedColsBase.push({
                  id: 'EndDate',
                  header: 'END DATE',
                  accessorKey: 'EndDate',
                  enableColumnFilter: true,
                  filterFn: 'contains',
                  size: 140,
                  Cell: ({ row }) => (row?.original?.EndDate ? row.original.EndDate.split('T')[0] : ''),
                });
              }

              // Ensure RequestType exists
              const hasReq = unplannedColsBase.find((c) => (c.id || c.accessorKey) === 'RequestType');
              if (!hasReq) {
                unplannedColsBase.push({
                  id: 'RequestType',
                  header: 'REQUEST TYPE',
                  accessorKey: 'RequestType',
                  enableColumnFilter: true,
                  filterFn: 'contains',
                  size: 140,
                });
              }

              // Build ordered columns with EffectiveDate renamed to Start Date and adjacent EndDate/RequestType
              const desiredOrder = [
                'ScheduleChangeRequestId',
                'RequestedBy',
                'Requestor',
                'CreatedDate',
                'EffectiveDate', // will be labeled Start Date
                'EndDate',
                'RequestType',
                'Location',
                'Status',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
                'Reason',
                'OfficeNumber',
                'Actions',
              ];

              const orderedCols = desiredOrder
                .map((key) => {
                  const col = unplannedColsBase.find((c) => (c.id || c.accessorKey) === key);
                  if (!col) return null;
                  // override EffectiveDate header and cell
                  if ((col.id || col.accessorKey) === 'EffectiveDate') {
                    return {
                      ...col,
                      header: 'START DATE',
                      Cell: ({ row }) => (row?.original?.EffectiveDate ? row.original.EffectiveDate.split('T')[0] : ''),
                    };
                  }
                  if ((col.id || col.accessorKey) === 'EndDate') {
                    return {
                      ...col,
                      header: 'END DATE',
                      Cell: ({ row }) => (row?.original?.EndDate ? row.original.EndDate.split('T')[0] : ''),
                    };
                  }
                  return col;
                })
                .filter(Boolean);

              // Append any remaining base columns (days, actions, etc.) so they are not lost
              const remainingCols = unplannedColsBase.filter(
                (c) => !orderedCols.find((oc) => (oc?.id || oc?.accessorKey) === (c.id || c.accessorKey))
              );
              const finalCols = [...orderedCols, ...remainingCols];

              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <ExportImportButton
                      type={'link'}
                      loading={exportLoading}
                      icon={<img alt='export' src={'./images/export.svg'} />}
                      onClick={handleExport}
                    >
                      <ExportImportButtonText>{exportLoading ? 'Exporting' : 'Export'}</ExportImportButtonText>
                    </ExportImportButton>
                  </div>
                  <GenericGrid
                    columns={finalCols}
                    columnOrder={finalCols
                      .filter(Boolean)
                      .map((c) => ((c as any).id || (c as any).accessorKey) as string)}
                    data={temporaryData}
                    pagination={{ pageIndex: 0, pageSize: TEMPORARY_REQUEST_DEFAULT_PAGE_SIZE }}
                    sorting={TEMPORARY_REQUEST_DEFAULT_SORTING}
                    onColumnFiltersChange={setGridColumnFilters}
                    onSortingChange={setGridSorting}
                    enableRowSelection={false}
                    rowSelection={[]}
                    columnPinning={{}}
                    filterDisplayMode='popover'
                    highlightFilterMatch={true}
                    paginationDisplayMode='pages'
                    paginationProps={{
                      color: 'primary',
                      shape: 'rounded',
                      showRowsPerPage: true,
                      variant: 'outlined',
                      rowsPerPageOptions: [10, 50, 100, 500, 1000],
                    }}
                    enableStickyHeader={true}
                    enableStickyFooter={true}
                    enableColumnPinning={true}
                    isLoading={loading}
                    enableAdd={isUnPlannedHooRequestAllowed || isUnplannedHooWriteAllowed}
                    onAddClick={handleAddClick}
                    muiTableBodyCellProps={{
                      sx: {
                        fontSize: '11px',
                        textAlign: 'left',
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                        padding: '6px 10px',
                        height: '40px',
                        lineHeight: '1.2',
                      },
                    }}
                    muiTableHeadCellProps={{
                      sx: {
                        fontSize: '11px',
                        fontWeight: 700,
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                        textAlign: 'center',
                        padding: '6px 10px',
                        height: '40px',
                        lineHeight: '1.2',
                      },
                    }}
                    muiTableBodyRowProps={{
                      sx: {
                        height: '40px',
                        '& td': {
                          padding: '6px 10px',
                          height: '40px',
                        },
                        textAlign: 'center',
                      },
                    }}
                    totalRecordsLabel='TOTAL REQUESTS'
                  />
                </>
              );
            })()}
          </Tabs.TabPane>
        )}
        {showCallForwardingTab && (
          <Tabs.TabPane tab='Call Forwarding' key='CallForwarding'>
            <CallForwardingSnapshot />
          </Tabs.TabPane>
        )}
      </Tabs>
      <AddHooRequestDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
        modalData={modalData}
        loading={loading}
        hooType={activeHooType}
      />
      <Modal
        title='Delete Permanent hours request Confirmation'
        okText='Ok'
        centered
        style={{ top: 20 }}
        open={openDelete}
        onOk={(e) => handleDelete(e)}
        onCancel={handleDeleteCancel}
      >
        <span>Are you sure you wish to cancel this request?</span>
      </Modal>
      <Modal
        title='Accept Permanent hours request Confirmation'
        okText='Ok'
        centered
        style={{ top: 20 }}
        open={openAccept}
        onOk={(e) => handleAccept(e)}
        onCancel={handleAcceptCancel}
      >
        <span>Are you sure you wish to accept this request?</span>
      </Modal>
      <Modal
        title='Reject Permanent hours request Confirmation'
        okText='Ok'
        centered
        style={{ top: 20 }}
        open={openReject}
        onOk={(e) => handleReject(e)}
        onCancel={handleRejectCancel}
      >
        <span>Are you sure you wish to reject this request?</span>
      </Modal>

      <Modal
        title='Reject Reason'
        okText='Ok'
        centered
        style={{ top: 20 }}
        open={openRejectReason}
        onOk={(e) => handleRejectReasonOk(e)}
        onCancel={handleRejectCancel}
      >
        <Form layout='vertical' form={rejectReasonForm}>
          <Form.Item label='Reason for Request Rejection' name='rejectReason'>
            <TextArea
              required
              rows={3}
              maxLength={4000}
              placeholder='Enter reject reason...'
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </React.Fragment>
  );
}

export default HooAccommodations;
